/* eslint-disable no-throw-literal */

import { isNil } from "lodash";
import { formatISODateOnly, formatISODateTime, formatISOTimeOnly } from "../components/util/helpers";
import { t } from "../i18n/util";
import { Credentials } from "../stores/AuthStore";
import {
    GetApplicantsParams,
    GetNotificationsParams,
    IPostApplicationStateTransitionPayload,
    PatchApplicantDetailsPayload,
    PostApplicantPayload,
    PostCodingSubmissionPayload
} from "../types/models";
import { apiClient } from "./APIClient";
import {
    ApplicationStateEnum,
    AppointmentProposal,
    AssetUploadResponse,
    GetApplicantDetailsResponse,
    GetApplicantsResponse,
    GetAppointmentsResponse,
    GetCodingSubmissionStatusResponse,
    GetGoogleResponse,
    GetNotificationsResponse,
    GetNotificationsUnreadCountResponse,
    GetUserInfoResponse,
    GetUsersResponse,
    PatchApplicantDetailsResponse,
    PatchApplicantDetailsResponseLanguageEnum,
    PostApplicantOptionalLinksPayload,
    PostApplicantResponse,
    PostApplicationStateTransitionPayload,
    PostApplicationStateTransitionResponse,
    PostLoginResponse,
    PutApplicantNoteCommentsPayload,
    PutApplicantNotesPayload
} from "./APITypes";

export const STATUS_CODE_UNAUTHORIZED = 401;
export const STATUS_CODE_BAD_REQUEST = 400;
export const STATUS_CODE_NOT_ALLOWED = 403;

export type TableParams = {
    keyword?: string;
    offset?: number;
    limit?: number;
    orderBy?: string;
    orderDir?: "asc" | "desc";
};

export const API = {
    getGoogleLogin(): Promise<GetGoogleResponse> {
        return apiClient.requestType({
            url: "/api/v1/auth/google",
            method: "GET"
        });
    },

    loginWithGoogle(code: string, state: string): Promise<Credentials> {
        return apiClient.requestType(
            {
                url: "/api/v1/auth/login-google",
                method: "POST",
                body: {
                    state,
                    code
                }
            },
            false
        );
    },

    postApplicant(applicant: PostApplicantPayload): Promise<PostApplicantResponse> {
        const data = new FormData();

        if (applicant.resume) {
            data.append("resumeFile", applicant.resume);
            data.append("resumeName", applicant.resume?.name);
        }
        if (applicant.video) {
            data.append("videoFile", applicant.video);
            data.append("videoName", applicant.video?.name);
        }
        if (applicant.photo) {
            data.append("photoFile", applicant.photo);
            data.append("photoName", applicant.photo?.name);
        }
        if (applicant.portfolio) {
            data.append("portfolioFile", applicant.portfolio);
            data.append("portfolioName", applicant.portfolio?.name);
        }
        if (applicant.motivationalLetter) {
            data.append("motivationalLetterFile", applicant.motivationalLetter);
            data.append("motivationalLetterName", applicant.motivationalLetter?.name);
        }
        if (applicant.optionalFiles.length > 0) {
            applicant.optionalFiles.forEach((file) => {
                data.append("optionalFileNames", file.name);
                data.append(file.name, file);
            });
        }
        if (applicant.optionalLinks.length > 0) {
            applicant.optionalLinks.forEach((link) => {
                data.append("optionalLinks", link);
            });
        }
        if (applicant.device) {
            data.append("device", applicant.device);
        }
        if (applicant.discovery) {
            if (applicant.discovery === "different_source" && applicant.customDiscoverySource) {
                data.append("discovery", applicant.customDiscoverySource);
            }
            data.append("discovery", applicant.discovery);
        }
        if (applicant.email) {
            data.append("email", applicant.email);
        }
        if (applicant.phone) {
            data.append("phone", applicant.phone);
        }
        if (!isNil(applicant.experience)) {
            data.append("experience", applicant.experience.toString());
        }
        if (applicant.gdprAccepted) {
            data.append("gdprAccepted", applicant.gdprAccepted.toString());
        }
        if (applicant.jobCategory) {
            data.append("jobCategory", applicant.jobCategory);
        }
        if (applicant.motivation) {
            data.append("motivation", applicant.motivation);
        }
        if (applicant.firstName) {
            data.append("firstName", applicant.firstName);
        }
        if (applicant.lastName) {
            data.append("lastName", applicant.lastName);
        }
        if (applicant.noticePeriod) {
            data.append("noticePeriod", applicant.noticePeriod.toString());
        }
        if (applicant.salary) {
            data.append("salary", applicant.salary.toString());
        }
        if (applicant.startDate) {
            data.append("startDate", formatISODateTime(applicant.startDate));
        }
        if (applicant.workHours) {
            data.append("workHours", Number(applicant.workHours.toString().replace(",", ".")).toString());
        }
        if (applicant.language) {
            data.append("language", applicant.language);
        }
        if (applicant.previousProjects) {
            data.append("previousProjects", applicant.previousProjects);
        }

        return apiClient.uploadFormDataJSON({
            url: "/api/v1/applicants",
            method: "POST",
            body: data
        }) as Promise<PostApplicantResponse>;
    },

    getApplicants(params?: GetApplicantsParams): Promise<GetApplicantsResponse> {
        return apiClient.requestType({
            url: "/api/v1/applicants",
            queryParameters: params
        });
    },

    getApplicant(applicantId: string): Promise<GetApplicantDetailsResponse> {
        if (!applicantId) {
            throw new Error(t("error.missingParams"));
        }

        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}`
        });
    },

    postOptionalLinks(applicantId: string, payload: PostApplicantOptionalLinksPayload) {
        return apiClient.request({
            url: `/api/v1/applicants/${applicantId}/optional-links`,
            method: "POST",
            body: payload
        });
    },

    patchApplicant(
        applicantId: string,
        applicant: PatchApplicantDetailsPayload & { customDiscoverySource?: string }
    ): Promise<PatchApplicantDetailsResponse> {
        const data = new FormData();

        if (applicant.resume) {
            data.append("resumeName", applicant.resume?.name);
            data.append("resumeFile", applicant.resume);
        }
        if (applicant.video) {
            data.append("videoName", applicant.video?.name);
            data.append("videoFile", applicant.video);
        }
        if (applicant.photo) {
            data.append("photoFile", applicant.photo);
            data.append("photoName", applicant.photo?.name);
        }
        if (applicant.portfolio) {
            data.append("portfolioFile", applicant.portfolio);
            data.append("portfolioName", applicant.portfolio?.name);
        }
        if (applicant.motivationalLetter) {
            data.append("motivationalLetterName", applicant.motivationalLetter?.name);
            data.append("motivationalLetterFile", applicant.motivationalLetter);
        }
        if (applicant.contract) {
            data.append("contractName", applicant.contract?.name);
            data.append("contractFile", applicant.contract);
        }
        if (applicant.optionalFiles && applicant.optionalFiles?.length > 0) {
            applicant.optionalFiles.forEach((file) => {
                data.append("optionalFileNames", file.name);
                data.append(file.name, file);
            });
        }
        if (applicant.device) {
            data.append("device", applicant.device);
        }
        if (applicant.discovery) {
            if (applicant.discovery === "different_source" && applicant.customDiscoverySource) {
                data.append("discovery", applicant.customDiscoverySource);
            }
            data.append("discovery", applicant.discovery);
        }
        if (applicant.email) {
            data.append("email", applicant.email);
        }
        if (applicant.phone) {
            data.append("phone", applicant.phone);
        }
        if (!isNil(applicant.experience)) {
            data.append("experience", applicant.experience.toString());
        }
        if (applicant.jobCategory) {
            data.append("jobCategory", applicant.jobCategory);
        }
        if (applicant.motivation) {
            data.append("motivation", applicant.motivation);
        }
        if (applicant.firstName) {
            data.append("firstName", applicant.firstName);
        }
        if (applicant.lastName) {
            data.append("lastName", applicant.lastName);
        }
        if (applicant.salary) {
            data.append("salary", applicant.salary.toString());
        }
        if (applicant.startDate) {
            data.append("startDate", formatISODateTime(applicant.startDate));
        }
        if (applicant.workHours) {
            data.append("workHours", Number(applicant.workHours.toString().replace(",", ".")).toString());
        }
        if (applicant.language) {
            data.append("language", applicant.language);
        }

        data.append("deviceSN", applicant.deviceSN ?? "");

        data.append("noticePeriod", (applicant.noticePeriod ?? 0).toString());

        data.append("previousProjects", applicant.previousProjects ?? "");

        data.append("personnelNumber", applicant.personnelNumber ?? "");

        if (applicant.shirtSize) {
            data.append("shirtSize", applicant.shirtSize);
        }

        return apiClient.uploadFormDataJSON({
            url: `/api/v1/applicants/${applicantId}`,
            method: "PATCH",
            body: data
        }) as Promise<PatchApplicantDetailsResponse>;
    },

    deleteApplicant(applicantId: string) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}`,
            method: "DELETE"
        });
    },

    postTransition(
        id: string,
        applicant: IPostApplicationStateTransitionPayload,
        applicationState: ApplicationStateEnum,
        contractFileId?: string
    ): Promise<PostApplicationStateTransitionResponse> {
        const body: PostApplicationStateTransitionPayload = {
            applicationState: applicationState,
            appointmentProposals: [],
            sendExternal: !applicant.noMessage,
            archived: applicant.evidence === "true",
            mailBody: applicant.message,
            onlineMeeting: applicant.onlineMeeting,
            contractFileId: contractFileId,
            evidenceUntil: formatISODateOnly(applicant.date),
            evidence: applicant.evidence === "true"
        };
        const tmpAppointmentProposals: AppointmentProposal[] = [];

        if (applicant.interviewDate1 && applicant.interviewStartTime1 && applicant.interviewEndTime1) {
            tmpAppointmentProposals.push({
                invitees: applicant.guests,
                title: applicant.title,
                startTime: new Date(
                    `${formatISODateOnly(applicant.interviewDate1)}T${formatISOTimeOnly(applicant.interviewStartTime1)}`
                ),
                endTime: new Date(
                    `${formatISODateOnly(applicant.interviewDate1)}T${formatISOTimeOnly(applicant.interviewEndTime1)}`
                )
            });
        }
        if (applicant.interviewDate2 && applicant.interviewStartTime2 && applicant.interviewEndTime2) {
            tmpAppointmentProposals.push({
                invitees: applicant.guests,
                title: applicant.title,
                startTime: new Date(
                    `${formatISODateOnly(applicant.interviewDate2)}T${formatISOTimeOnly(applicant.interviewStartTime2)}`
                ),
                endTime: new Date(
                    `${formatISODateOnly(applicant.interviewDate2)}T${formatISOTimeOnly(applicant.interviewEndTime2)}`
                )
            });
        }
        if (applicant.interviewDate3 && applicant.interviewStartTime3 && applicant.interviewEndTime3) {
            tmpAppointmentProposals.push({
                invitees: applicant.guests,
                title: applicant.title,
                startTime: new Date(
                    `${formatISODateOnly(applicant.interviewDate3)}T${formatISOTimeOnly(applicant.interviewStartTime3)}`
                ),
                endTime: new Date(
                    `${formatISODateOnly(applicant.interviewDate3)}T${formatISOTimeOnly(applicant.interviewEndTime3)}`
                )
            });
        }

        if (applicant.scheduledEmail && applicant.scheduledEmailDate) {
            body.scheduledEmail = formatISODateOnly(applicant.scheduledEmailDate);
        }

        body.appointmentProposals = tmpAppointmentProposals;

        return apiClient.requestType({
            url: `/api/v1/applicants/${id}/transition`,
            method: "POST",
            body: body
        });
    },

    getUsers(): Promise<GetUsersResponse> {
        return apiClient.requestType({
            url: "api/v1/users"
        });
    },

    getUserInfo(): Promise<GetUserInfoResponse> {
        return apiClient.requestType({
            url: "/api/v1/auth/userinfo"
        });
    },

    postLogout(refreshToken?: string) {
        const body = refreshToken ? { refresh_token: refreshToken } : {};

        return apiClient.request({
            url: "/api/v1/auth/logout",
            method: "POST",
            body: body
        });
    },

    refresh(refreshToken: string): Promise<PostLoginResponse> {
        const body = { refresh_token: refreshToken };

        return apiClient.requestType(
            {
                url: "/api/v1/auth/refresh",
                method: "POST",
                body: body
            },
            false
        );
    },

    getAsset(assetId: string) {
        return apiClient.requestType({
            url: `/api/v1/assets/${assetId}`
        });
    },

    postAsset(asset: File, language: PatchApplicantDetailsResponseLanguageEnum): Promise<AssetUploadResponse> {
        const data = new FormData();

        data.append("name", asset.name);
        data.append("file", asset);
        data.append("language", language);

        return apiClient.uploadFormDataJSON({
            url: "/api/v1/assets",
            method: "POST",
            body: data
        });
    },

    putNote(applicantId: string, body: PutApplicantNotesPayload) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/notes`,
            method: "PUT",
            body: body
        });
    },

    updateNote(applicantId: string, noteId: string, content: string, title: string) {
        const body = {
            content: content,
            title: title,
            encrypted: false
        };

        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/notes/${noteId}`,
            method: "PUT",
            body: body
        });
    },

    deleteNote(applicantId: string, noteId: string) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/notes/${noteId}`,
            method: "DELETE"
        });
    },

    deleteDocument(applicantId: string, documentId: string) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/document/${documentId}`,
            method: "DELETE"
        });
    },

    getUnreadNotificationCount(): Promise<GetNotificationsUnreadCountResponse> {
        return apiClient.requestType({
            url: `/api/v1/notifications/unread-count`
        });
    },

    postReadNotifications() {
        return apiClient.request({
            url: `/api/v1/notifications/read`,
            method: "POST"
        });
    },

    getAppointments(applicantId: string): Promise<GetAppointmentsResponse> {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/appointments`
        });
    },

    postAppointments(applicantId: string, appointments: string[]) {
        const body = {
            ids: appointments
        };

        return apiClient.request({
            url: `/api/v1/applicants/${applicantId}/appointments`,
            method: "POST",
            body: body
        });
    },

    postAppointment(applicantId: string, appointmentId: string) {
        return apiClient.request({
            url: `/api/v1/applicants/${applicantId}/appointment/${appointmentId}`,
            method: "POST",
            body: {
                sendExternal: false
            }
        });
    },

    getNotifications(params?: GetNotificationsParams): Promise<GetNotificationsResponse> {
        return apiClient.requestType({
            url: `/api/v1/notifications`,
            queryParameters: params
        });
    },

    getCodingSubmissionStatus(applicantId: string, codingId: string): Promise<GetCodingSubmissionStatusResponse> {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/coding-submission/${codingId}/status`
        });
    },

    postCodingSubmission(applicantId: string, codingId: string, codingSubmission: PostCodingSubmissionPayload) {
        const data = new FormData();

        if (codingSubmission.comments) {
            data.append("comments", codingSubmission.comments);
        }
        if (codingSubmission.archiveFile) {
            data.append("archiveName", codingSubmission.archiveFile.name);
            data.append("archiveFile", codingSubmission.archiveFile);
        }

        return apiClient.uploadFormDataJSON({
            url: `/api/v1/applicants/${applicantId}/coding-submission/${codingId}`,
            method: "POST",
            body: data
        });
    },

    postReadApplicant(applicantId: string) {
        return apiClient.request({
            url: `/api/v1/applicants/${applicantId}/read`,
            method: "POST"
        });
    },

    putNoteComments(applicantId: string, noteId: string, payload: PutApplicantNoteCommentsPayload) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/notes/${noteId}/comments`,
            method: "PUT",
            body: payload
        });
    },

    putNoteComment(applicantId: string, noteId: string, commentId: string, payload: PutApplicantNoteCommentsPayload) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/notes/${noteId}/comments/${commentId}`,
            method: "PUT",
            body: payload
        });
    },

    deleteComment(applicantId: string, noteId: string, commentId: string) {
        return apiClient.requestType({
            url: `/api/v1/applicants/${applicantId}/notes/${noteId}/comments/${commentId}`,
            method: "DELETE"
        });
    }
};
